import { newsList, resourceList, topicList } from "@/mocks/content";
import type { NewsItem, ResourceItem, TopicItem } from "@/mocks/content";

export type ContentKind = "news" | "resource" | "topic";

export interface ContentMeta {
  label: string;
  value: string;
}

export interface ContentDetail {
  id: string;
  kind: ContentKind;
  title: string;
  cover: string;
  summary: string;
  body: string[];
  date: string;
  author: string;
  source: string;
  category: string;
  tags: string[];
  meta: ContentMeta[];
  backTo: string;
  backLabel: string;
  rating?: number;
}

export interface SearchResult {
  id: string;
  kind: ContentKind;
  title: string;
  summary: string;
  cover: string;
  date: string;
  label: string;
  href: string;
}

const kindLabel: Record<ContentKind, string> = {
  news: "资讯",
  resource: "资源",
  topic: "话题",
};

export function formatNumber(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return String(value);
}

export function shortDate(date: string): string {
  return date.replace(/-/g, ".");
}

function fromNews(item: NewsItem): ContentDetail {
  return {
    id: item.id,
    kind: "news",
    title: item.title,
    cover: item.cover,
    summary: item.summary,
    body: item.body,
    date: item.date,
    author: item.author,
    source: item.source,
    category: item.category,
    tags: [item.category, "资讯"],
    meta: [
      { label: "发布时间", value: item.date },
      { label: "作者", value: item.author },
      { label: "来源", value: item.source },
      { label: "分类", value: item.category },
    ],
    backTo: `/news/${encodeURIComponent(item.category)}`,
    backLabel: `返回${item.category}`,
  };
}

function fromResource(item: ResourceItem): ContentDetail {
  return {
    id: item.id,
    kind: "resource",
    title: `${item.name} · ${item.typeLabel}资源介绍`,
    cover: item.cover,
    summary: item.summary,
    body: item.body,
    date: String(item.year),
    author: "18禁资源组",
    source: "18禁资源库",
    category: item.typeLabel,
    tags: item.tags,
    meta: [
      { label: "类型", value: item.typeLabel },
      { label: "地区", value: item.region },
      { label: "年份", value: String(item.year) },
      { label: "推荐度", value: `${item.rating} / 10` },
    ],
    backTo: `/resources/${item.type}`,
    backLabel: `返回${item.typeLabel}资源`,
    rating: item.rating,
  };
}

function fromTopic(item: TopicItem): ContentDetail {
  return {
    id: item.id,
    kind: "topic",
    title: item.title,
    cover: "",
    summary: item.summary,
    body: item.body,
    date: item.date,
    author: item.author,
    source: "18禁话题区",
    category: "话题讨论",
    tags: item.tags,
    meta: [
      { label: "发起人", value: item.author },
      { label: "发布时间", value: item.date },
      { label: "回复数", value: String(item.replies) },
      { label: "浏览", value: String(item.views) },
    ],
    backTo: "/topics",
    backLabel: "返回话题列表",
  };
}

export function getContentById(id?: string): ContentDetail | null {
  if (!id) return null;
  const news = newsList.find((item) => item.id === id);
  if (news) return fromNews(news);
  const resource = resourceList.find((item) => item.id === id);
  if (resource) return fromResource(resource);
  const topic = topicList.find((item) => item.id === id);
  if (topic) return fromTopic(topic);
  return null;
}

export function searchContent(query: string, kinds: ContentKind[] = ["news", "resource", "topic"]): SearchResult[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  const results: SearchResult[] = [];

  if (kinds.includes("news")) {
    newsList.forEach((item) => {
      const haystack = `${item.title} ${item.summary} ${item.category}`.toLowerCase();
      if (haystack.includes(keyword)) {
        results.push({
          id: item.id,
          kind: "news",
          title: item.title,
          summary: item.summary,
          cover: item.cover,
          date: item.date,
          label: item.category,
          href: `/detail/${item.id}`,
        });
      }
    });
  }

  if (kinds.includes("resource")) {
    resourceList.forEach((item) => {
      const haystack = `${item.name} ${item.summary} ${item.tags.join(" ")} ${item.region}`.toLowerCase();
      if (haystack.includes(keyword)) {
        results.push({
          id: item.id,
          kind: "resource",
          title: item.name,
          summary: item.summary,
          cover: item.cover,
          date: String(item.year),
          label: item.typeLabel,
          href: `/detail/${item.id}`,
        });
      }
    });
  }

  if (kinds.includes("topic")) {
    topicList.forEach((item) => {
      const haystack = `${item.title} ${item.summary} ${item.tags.join(" ")}`.toLowerCase();
      if (haystack.includes(keyword)) {
        results.push({
          id: item.id,
          kind: "topic",
          title: item.title,
          summary: item.summary,
          cover: item.cover ?? "",
          date: item.date,
          label: "话题",
          href: `/detail/${item.id}`,
        });
      }
    });
  }

  return results;
}

export function getRelatedContent(current: ContentDetail, limit = 4): ContentDetail[] {
  const pool: ContentDetail[] = [];

  if (current.kind === "resource") {
    const resource = resourceList.find((item) => item.id === current.id);
    resourceList
      .filter((item) => item.id !== current.id)
      .forEach((item) => {
        const overlap = item.tags.filter((tag) => resource?.tags.includes(tag)).length;
        const bonus = item.type === resource?.type ? 2 : 0;
        if (overlap + bonus > 0) pool.push(fromResource(item));
      });
    newsList.slice(0, 2).forEach((item) => pool.push(fromNews(item)));
  } else {
    const tags = current.tags;
    const candidates: ContentDetail[] = [
      ...newsList.map(fromNews),
      ...resourceList.map(fromResource),
      ...topicList.map(fromTopic),
    ];
    candidates
      .filter((item) => item.id !== current.id)
      .forEach((item) => {
        const overlap = item.tags.filter((tag) => tags.includes(tag)).length;
        if (overlap > 0 || item.kind === current.kind) pool.push(item);
      });
  }

  return pool.slice(0, limit);
}

export function kindName(kind: ContentKind): string {
  return kindLabel[kind];
}